import React, {FunctionComponent, useState} from 'react';
import {useAppDispatch} from "../../../../store/hooks";
import {useTranslation} from "react-i18next";
import './style.scss'
import {CircularProgress} from "@mui/material";
import {TextField} from "../../../../components/StyledComponents";
import {
  closeModalIcon,
  errorBigIcon,
  toastSuccess
} from "../../../../assets/icons/icons";
import {Button} from "../../../../components/StyledComponents";
import {cancelOneSubscription, cancelTrial, getUser, sendOffboardingResults} from "../../../../store/actions";
import Swal from "sweetalert2";
import {IError, IOffboardPayload} from "../../../../models/inner-models";
import {decoratePopUpMessage} from "../../../../utils/popUpTextDecorator";
import RadioButton from "../../../../components/Buttons/RadioButton";

interface IOffboardProps {
  platform: string;
  period: string;
  isTrial: boolean;
  onClose: () => void;
}

const radioList = [
  {value: "missing_features", label: "There are missing features"},
  {value: "difficult_to_use", label: "Difficult to use"},
  {value: "no_need_anymore", label: "No need to use the product anymore"},
  {value: "switched_to_another", label: "Switched to another Backup app"},
  {value: "cost_too_high", label: "The cost is too high"},
  {value: "technical_bugs", label: "There are technical bugs"},
  {value: "other", label: "Other"},
]

const Offboarding: FunctionComponent<IOffboardProps> = ({platform, period, isTrial, onClose}: IOffboardProps) => {
  const dispatch = useAppDispatch();
  const {t: translate} = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [process, setProcess] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [selectRadio, setSelectRadio] = useState<string | null>(null);
  const [input, setInput] = useState<string>('');
  const [text, setText] = useState<string>('');

  const onConfirmStopSub = async () => {
    try {
      setProcess(true);
      const payload: IOffboardPayload = {
        platform: platform,
        answer: selectRadio || '',
        missing_features: input,
        improve: text
      }
      isTrial ?
        await dispatch(cancelTrial(payload)) :
        await dispatch(cancelOneSubscription(platform)).unwrap();
      await dispatch(getUser()).unwrap()

      Swal.fire({
        title: translate('notifications.titles.success'),
        text: translate('notifications.subscription.canceled_successfully'),
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
      });
      // location.reload();
      if (!isTrial) sendPoll()
      setStep(step + 1)
      setProcess(false);
    } catch (err) {
      const error = err as IError;
      setProcess(false);
      onClose()
      await Swal.fire({
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: errorBigIcon,
      });
    }
  };

  const sendPoll = () => {
    const payload: IOffboardPayload = {
      platform: platform,
      answer: selectRadio || '',
      missing_features: input,
      improve: text
    }
    dispatch(sendOffboardingResults(payload))
  }

  const nextStep = async () => {
    try {
      if (step === 3) {
        await onConfirmStopSub()
      }
      if (step === 4) {
        onClose()
      } else if (step !== 3) setStep(step + 1);
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <div className='offboard'>
      <div className='offboard-bg' onClick={onClose}/>
      <div className='offboard-wrapper'>
        <div className='offboard-close' onClick={onClose}>
          <img src={closeModalIcon} loading="lazy"/>
        </div>
        {loading ?
          <div className="spinner-wrapper">
            <CircularProgress/>
          </div> :
          <div className='offboard-container'>
            <div className='offboard-title'>
              {
                step === 1 ?
                  isTrial ? 'Are you sure you wish to cancel your trial?' : 'Are you sure you wish to cancel your subscription?' :
                  step === 2 ?
                    'We are sad to see you go!' :
                    step === 3 ?
                      'What should we improve?' :
                      'Thank you!'
              }
            </div>
            <div className={process ? 'offboard-main with-blur' : 'offboard-main'}>
              {step === 1 &&
                (isTrial ?
                    <div>Your trial subscription will be cancelled immediately.</div> :
                    <div>
                      You will still continue to have access until your subscription expires on
                      &#32;<b>{period}.</b>
                    </div>
              )}
              {step === 2 && (
                <div>
                  <div className='offboard-main-title'>
                    We are always working to improve our service, and your feedback plays a key role. Why did you decide to cancel your subscription?
                  </div>
                  <div className='offboard-radio'>
                    {radioList.map(item => (
                      <label className="option" key={item.value}>
                        <RadioButton
                          name="reason"
                          value={item.value}
                          checked={selectRadio === item.value}
                          onClick={(e) => setSelectRadio(e.target.value)}
                        />
                        <span>{item.label}</span>
                        {(selectRadio === 'missing_features' || selectRadio === 'other') && selectRadio === item.value && (
                          <TextField
                            value={input}
                            placeholder='I wish this had...'
                            className='sub-input'
                            id="name"
                            type="text"
                            onChange={(e) => setInput(e.target.value)}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {step === 3 && (
                <div>
                  <div className='offboard-main-title'>
                    We are always working to improve our service and your feedback plays a key role.
                  </div>
                  <div className='offboard-radio'>
                    <TextField
                      multiline
                      value={text}
                      placeholder='It will be better if...'
                      className='sub-text'
                      id="name"
                      type="text"
                      rows={4}
                      onChange={(e) => setText(e.target.value)}
                    />
                  </div>
                </div>
              )}
              {step === 4 && (
                <div className='offboard-main-title'>
                  We appreciate your feedback and look forward to welcoming you back in the future.
                </div>
              )}
            </div>
            <div className='offboard-action'>
              {step !== 4 && (
                <Button
                  type="submit" variant="outlined" color="secondary"
                  mr={3}
                  onClick={()=>{
                    onClose()
                    if (step !== 1) sendPoll()
                  }}
                  disabled={process}
                >
                  {translate('notifications.choices.cancel')}
                </Button>
              )}
              <Button
                type="submit" variant="contained" color="primary"
                disabled={process || (!selectRadio && step === 2) || (!input && step === 2 && selectRadio === 'other')}
                onClick={() => {
                    nextStep()
                }}
              >
                {process && (
                  <div className="small-spinner-wrapper">
                    <CircularProgress color="inherit" style={{width: '20px', height: '20px', marginTop: '6px'}}/>
                  </div>
                )}
                {step === 3 ? translate('notifications.choices.confirm') : step === 4 ? translate('notifications.choices.close') : 'Next'}
              </Button>
            </div>
          </div>
        }
      </div>
    </div>
  )
}

export default Offboarding;
